import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { 
  Button, CssBaseline, TextField, Link, Grid, Box, Typography, Container, 
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, 
  Snackbar, Alert, CircularProgress, Tooltip, 
  Paper, useTheme, InputAdornment, alpha, useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://www.entrinsic.io/">
        Entrinsic Connect
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#3a7bd5',
    },
    secondary: {
      main: '#00b8d4',
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const PartnerSignUpForm = ({ setShowDialog, setShowAlert, setAlertMessage, navigate, referringPartner }) => {
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [signingUp, setSigningUp] = useState(false);
  const [emailError, setEmailError] = useState("");
  const formRef = React.useRef(null);
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [companyNumberError, setCompanyNumberError] = useState("");
  const theme = useTheme();

  const isStrongPassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/;
    return regex.test(password);
  };

  const isValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email);
  };
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formRef.current) return;

    const data = new FormData(formRef.current);

    const firstName = data.get('firstName');
    const lastName = data.get('lastName');
    const company = data.get('company');
    const companyNumber = data.get('companyNumber');
    const email = data.get('email');
    const password = data.get('password');
    const confirmPassword = data.get('confirmPassword');

    // Reset errors
    setFirstNameError("");
    setLastNameError("");
    setCompanyError("");
    setCompanyNumberError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");

    // Validation
    if (!firstName.trim()) {
      setFirstNameError("Please enter your first name.");
      return;
    }

    if (!lastName.trim()) {
      setLastNameError("Please enter your last name.");
      return;
    }

    if (!company.trim()) {
      setCompanyError("Please enter your company name.");
      return;
    }

    if (!companyNumber.trim()) {
      setCompanyNumberError("Please enter your company number.");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    if (!isStrongPassword(password)) {
      setPasswordError("Password should be strong with at least 8 characters, including uppercase, lowercase, number, and a special character.");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match!");
      return;
    }

    try {
      setSigningUp(true);
      const response = await fetch('/api/partners/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: firstName,
          lastName: lastName,
          email: email,
          company: company,
          companyNumber: companyNumber,
          password: password,
          referringPartnerId: referringPartner?.id
        })
      });

      const result = await response.json();

      if (result.success) {
        setShowDialog(true);
      } else {
        setAlertMessage(result.message || "Partner sign up failed. Please try again.");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error during partner sign up:", error);
      setAlertMessage("An error occurred during partner sign up. Please try again later.");
      setShowAlert(true);
    } finally {
      setSigningUp(false);
    }
  };

  return (
    <Box 
      component="form" 
      ref={formRef} 
      noValidate 
      onSubmit={handleSubmit} 
      sx={{ 
        mt: 3, 
        width: '100%',
        '& .MuiTextField-root': {
          mb: 1
        }
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            autoComplete="given-name"
            name="firstName"
            required
            fullWidth
            id="firstName"
            label="First Name"
            autoFocus
            error={Boolean(firstNameError)}
            helperText={firstNameError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            error={Boolean(lastNameError)}
            helperText={lastNameError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="company"
            label="Company Name"
            name="company"
            autoComplete="organization"
            error={Boolean(companyError)}
            helperText={companyError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="companyNumber"
            label="Company Number"
            name="companyNumber"
            autoComplete="organization-number"
            error={Boolean(companyNumberError)}
            helperText={companyNumberError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            error={Boolean(emailError)}
            helperText={emailError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="new-password"
            error={Boolean(passwordError)}
            helperText={passwordError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            error={Boolean(confirmPasswordError)}
            helperText={confirmPasswordError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.1)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(58, 123, 213, 0.2)'
                }
              }
            }}
          />
        </Grid>
      </Grid>
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ 
          mt: 3, 
          mb: 2,
          py: 1.5,
          borderRadius: 2,
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(58, 123, 213, 0.2)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(58, 123, 213, 0.3)',
            transform: 'translateY(-2px)'
          }
        }}
        disabled={signingUp}
      >
        {signingUp ? <CircularProgress size={24} /> : 'Sign Up as Partner'}
      </Button>
      
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Link 
            href="/login" 
            variant="body2"
            sx={{
              textDecoration: 'none',
              color: theme.palette.primary.main,
              fontWeight: 500,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: theme.palette.primary.dark,
                textDecoration: 'underline'
              }
            }}
          >
            Already have an account? Sign in
          </Link>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Link 
            href="/signup" 
            variant="body2"
            sx={{
              textDecoration: 'none',
              color: theme.palette.secondary.main,
              fontWeight: 500,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: theme.palette.secondary.dark,
                textDecoration: 'underline'
              }
            }}
          >
            Need a regular account? Sign up here
          </Link>
        </Grid>
      </Grid>
    </Box>
  );
};

export default function PartnerSignUpPage() {
  const [showDialog, setShowDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [referringPartner, setReferringPartner] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch referring partner details if partner code is provided
  useEffect(() => {
    const partnerCode = searchParams.get('partner');
    if (partnerCode) {
      setLoadingPartner(true);
      fetch(`/api/partners/lookup/${partnerCode}`)
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setReferringPartner(data.partner);
          }
        })
        .catch(error => {
          console.error('Error fetching referring partner:', error);
        })
        .finally(() => {
          setLoadingPartner(false);
        });
    }
  }, [searchParams]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
          py: 4
        }}
      >
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              background: 'white',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #3a7bd5 0%, #00b8d4 100%)',
              }
            }}
          >
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                <img 
                  src={referringPartner?.PartnerLogo || '/Entrinsic_Logo.png'} 
                  alt={`${referringPartner?.name || 'Entrinsic'} Logo`}
                  style={{ 
                    maxWidth: isMobile ? '280px' : '500px',
                    height: 'auto',
                    marginBottom: '16px'
                  }}
                />
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1
                }}
              >
                Partner Sign up
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                align="center" 
                sx={{ 
                  maxWidth: '500px',
                  mx: 'auto',
                  mb: 3
                }}
              >
                Please enter your details below to register as a partner with {referringPartner?.name || 'Entrinsic Connect'}. 
                You will receive an email to verify your account, and then your account will 
                need to be approved before you can access partner features.
              </Typography>
            </Box>
            
            <PartnerSignUpForm 
              setShowDialog={setShowDialog} 
              setShowAlert={setShowAlert} 
              setAlertMessage={setAlertMessage} 
              navigate={navigate} 
              referringPartner={referringPartner}
            />
          </Paper>
          
          <Dialog
            open={showDialog}
            onClose={() => setShowDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <DialogTitle id="alert-dialog-title">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedUserIcon color="success" />
                <Typography variant="h6">Partner Registration Successful</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Your partner account has been created successfully. Please check your email 
                for a verification link to complete the signup process. After email verification, 
                your account will need approval from our team before you can access partner features.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={() => {
                  setShowDialog(false);
                  navigate("/login"); 
                }} 
                color="primary"
                variant="contained"
                sx={{ 
                  borderRadius: 2,
                  px: 3
                }}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar 
            open={showAlert} 
            autoHideDuration={6000} 
            onClose={() => setShowAlert(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setShowAlert(false)} 
              severity="error" 
              variant="filled"
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              {alertMessage}
            </Alert>
          </Snackbar>

          <Copyright sx={{ mt: 4, mb: 2, color: 'rgba(0,0,0,0.6)' }} />
        </Container>
      </Box>
    </ThemeProvider>
  );
}